import {
	CSSProperties,
	ReactElement,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

type ZoomableType = {
	children: ReactElement | ReactElement[];
	className?: string;
	zIndex?: number;
	style?: CSSProperties;
	maxScale?: number; // Same as CSS transform scale
	onTouchStart?: (event: TouchEvent) => void;
	onTouchMove?: (event: TouchEvent) => void;
	onTouchEnd?: (event: TouchEvent) => void;
};

// Instagram-like pinch-to-zoom:
// - Pinch to zoom, centered on the pinch midpoint
// - Pan with one finger when zoomed in
// - Reset to 1x when gesture ends and scale is back to 1
export const Zoomable = (props: ZoomableType) => {
	const {
		children,
		onTouchStart,
		onTouchMove,
		onTouchEnd,
		className,
		maxScale,
		zIndex: zIndexProp,
		style: styleProp,
	} = props;

	const effectiveMaxScale: number = maxScale ?? 3;
	const zIndex = zIndexProp ?? Number.MAX_SAFE_INTEGER;
	const style = styleProp ?? {};

	const elRef = useRef<HTMLDivElement | null>(null);

	// Render state
	const [scale, setScale] = useState(1);
	const [translateX, setTranslateX] = useState(0);
	const [translateY, setTranslateY] = useState(0);
	const [isMoving, setIsMoving] = useState(false);

	// Session refs
	const pinchStartDistanceRef = useRef(0);
	const pinchStartScaleRef = useRef(1);
	const pinchStartCenterRef = useRef({ x: 0, y: 0 });
	const pinchStartTranslateRef = useRef({ x: 0, y: 0 });

	const panLastRef = useRef<{ x: number; y: number } | null>(null);
	const scaleRef = useRef(1);
	const translateRef = useRef({ x: 0, y: 0 });

	const clamp = (val: number, min: number, max: number) =>
		Math.min(max, Math.max(min, val));

	const getTouchesInfo = (e: TouchEvent) => {
		const box = (e.target as HTMLElement)?.getBoundingClientRect();
		const t1 = e.touches?.[0];
		const t2 = e.touches?.[1];
		if (!t1 || !t2) return null;
		const distance = Math.hypot(
			t2.clientX - t1.clientX,
			t2.clientY - t1.clientY
		);
		const centerAbsolute = {
			x: (t1.clientX + t2.clientX) / 2,
			y: (t1.clientY + t2.clientY) / 2,
		};
		const centerRelative = {
			x: centerAbsolute.x - box.left,
			y: centerAbsolute.y - box.top,
		};
		return { distance, centerAbsolute, centerRelative };
	};

	const applyTransform = (s: number, tx: number, ty: number) => {
		scaleRef.current = s;
		translateRef.current = { x: tx, y: ty };
		setScale(s);
		setTranslateX(tx);
		setTranslateY(ty);
	};

	const handlePinchStart = (e: TouchEvent) => {
		e.preventDefault();
		const info = getTouchesInfo(e);
		if (!info) return;
		setIsMoving(true);
		pinchStartDistanceRef.current = info.distance;
		pinchStartScaleRef.current = scaleRef.current;
		pinchStartCenterRef.current = info.centerAbsolute;
		pinchStartTranslateRef.current = { ...translateRef.current };
	};

	const handlePinchMove = (e: TouchEvent) => {
		e.preventDefault();
		const info = getTouchesInfo(e);
		if (!info || pinchStartDistanceRef.current <= 0) return;

		const ratio = info.distance / pinchStartDistanceRef.current;
		if (!Number.isFinite(ratio) || ratio <= 0) return;

		const nextScale = clamp(
			pinchStartScaleRef.current * ratio,
			1,
			effectiveMaxScale
		);

		// Translate follows the movement of the pinch center
		const deltaX = info.centerAbsolute.x - pinchStartCenterRef.current.x;
		const deltaY = info.centerAbsolute.y - pinchStartCenterRef.current.y;

		const nextTx = pinchStartTranslateRef.current.x + deltaX;
		const nextTy = pinchStartTranslateRef.current.y + deltaY;

		applyTransform(nextScale, nextTx, nextTy);
	};

	const handlePanStart = (e: TouchEvent) => {
		const t = e.touches?.[0];
		if (!t) return;
		panLastRef.current = { x: t.clientX, y: t.clientY };
		setIsMoving(true);
	};

	const handlePanMove = (e: TouchEvent) => {
		const t = e.touches?.[0];
		if (!t || !panLastRef.current) return;
		const dx = t.clientX - panLastRef.current.x;
		const dy = t.clientY - panLastRef.current.y;
		panLastRef.current = { x: t.clientX, y: t.clientY };
		applyTransform(
			scaleRef.current,
			translateRef.current.x + dx,
			translateRef.current.y + dy
		);
	};

	const endGesture = () => {
		setIsMoving(false);
		panLastRef.current = null;
		pinchStartDistanceRef.current = 0;
		pinchStartScaleRef.current = scaleRef.current;
		pinchStartCenterRef.current = { x: 0, y: 0 };
		pinchStartTranslateRef.current = { ...translateRef.current };
		if (scaleRef.current <= 1.01) {
			applyTransform(1, 0, 0);
		}
	};

	const touchStart = useCallback(
		(e: TouchEvent) => {
			if (e.touches.length === 2) {
				handlePinchStart(e);
			} else if (e.touches.length === 1 && scaleRef.current > 1) {
				handlePanStart(e);
			}
			if (typeof onTouchStart === 'function') onTouchStart(e);
		},
		[onTouchStart]
	);

	const touchMove = useCallback(
		(e: TouchEvent) => {
			if (e.touches.length === 2) {
				handlePinchMove(e);
			} else if (e.touches.length === 1 && scaleRef.current > 1) {
				handlePanMove(e);
			}
			if (typeof onTouchMove === 'function') onTouchMove(e);
		},
		[onTouchMove]
	);

	const touchEnd = useCallback(
		(e: TouchEvent) => {
			if (e.touches.length === 0) {
				endGesture();
			}
			if (typeof onTouchEnd === 'function') onTouchEnd(e);
		},
		[onTouchEnd]
	);

	useEffect(() => {
		const el = elRef.current;
		if (!el) return;

		el.addEventListener('touchstart', touchStart, { passive: false });
		el.addEventListener('touchmove', touchMove, { passive: false });
		el.addEventListener('touchend', touchEnd, { passive: false });
		el.addEventListener('touchcancel', touchEnd, { passive: false });

		return () => {
			el.removeEventListener('touchstart', touchStart);
			el.removeEventListener('touchmove', touchMove);
			el.removeEventListener('touchend', touchEnd);
			el.removeEventListener('touchcancel', touchEnd);
		};
	}, [touchStart, touchMove, touchEnd]);

	const shouldDisableNativeGestures = isMoving || scale > 1;
	const touchAction = shouldDisableNativeGestures ? 'none' : 'pan-y';

	return (
		<div
			ref={elRef}
			style={{
				position: isMoving ? 'relative' : undefined,
				zIndex: isMoving ? zIndex : undefined,
				transformOrigin: 'center center',
				transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
				touchAction,
				...style,
			}}
			className={className}
		>
			{children}
		</div>
	);
};
